import React from 'react';
import { Table } from 'reactstrap';
import StatusBadge from '../../../../Components/Common/StatusBadge';
import { OperationalToleranceSectionValue } from '../../../../shared/offer-domain/ui';
import { getAlphaImprovementFieldValue } from '../../../../shared/offer-domain/improvement';
import {
  getProcurementProductNameId,
  usesDyedUndyedAlpha,
} from '../../../../shared/offer-domain/constants/product';
import { displayValue } from '../../../../utils/format';
import {
  formatIncotermRoute,
  formatParcelSize,
  formatPortLocation,
} from '../../../TenderEvaluation/utils/deliveryDetailUtils';
import { formatPrice } from '../../utils/formatters';
import {
  bidDetail,
  buildPortSlots,
  isCounterBid,
  offerToleranceProcurement,
  parcelUnit,
} from '../../utils/roomSpec';
import { PortCell, SpecHead } from './RequirementTable';
import RoomPanel from './RoomPanel';

const text = value => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

const capableFlag = value =>
  value === true || value === 'true' || value === 1 || value === '1';

const baseAlpha = (detail, field) => {
  const keys =
    field === 'dyed'
      ? ['dyed', 'Dyed', 'unitBbl', 'alphaDyed', 'AlphaDyed']
      : field === 'undyed'
        ? ['undyed', 'Undyed', 'cbm', 'alphaUndyed', 'AlphaUndyed']
        : ['alpha', 'Alpha', 'alphaMitra1', 'AlphaMitra1', 'alpha1', 'Alpha1'];
  return keys.map(key => text(detail?.[key])).find(Boolean) || '';
};

const showAlpha = (value, unit) => {
  const raw = text(value);
  if (!raw) return '-';
  if (/USD|BBL|MT/i.test(raw)) return raw;
  return formatPrice(raw, unit);
};

const BidStatus = ({ counter, capable }) => {
  if (counter) return <StatusBadge tone='warning' label='Counter' size='sm' />;
  if (capable)
    return <StatusBadge tone='primary' label='Compliant' size='sm' />;
  return <span className='text-muted'>-</span>;
};

const isGasoline = procurement =>
  usesDyedUndyedAlpha(getProcurementProductNameId(procurement)) ||
  /gasoline/i.test(String(procurement?.productName || ''));

const variantKind = (line, detail) => {
  const dyed = Boolean(baseAlpha(detail, 'dyed'));
  const undyed = Boolean(baseAlpha(detail, 'undyed'));
  if (dyed && undyed) return 'both';
  if (undyed) return 'undyed';
  if (dyed) return 'dyed';
  const label = String(line?.label || '').toLowerCase();
  if (label.includes('undyed')) return 'undyed';
  if (label.includes('dyed')) return 'dyed';
  return null;
};

const BidSpecTable = ({ partner, procurement, unit }) => {
  const counter = isCounterBid(partner);
  const kind = counter ? 'counter' : 'offer';
  const lines = partner?.lines?.length ? partner.lines : [null];
  const pairs = lines.map(line => {
    const detail = bidDetail(partner, line);
    return { line, detail, kind: variantKind(line, detail) };
  });
  const dyedPairs = pairs.filter(pair => pair.kind === 'dyed');
  const undyedPairs = pairs.filter(pair => pair.kind === 'undyed');
  const mergeVariants =
    (isGasoline(procurement) || dyedPairs.length + undyedPairs.length > 0) &&
    dyedPairs.length > 0 &&
    undyedPairs.length > 0;
  const duplicateBoth =
    pairs.length > 1 && pairs.every(pair => pair.kind === 'both');
  const groups = mergeVariants
    ? [
        [...dyedPairs, ...undyedPairs],
        ...pairs
          .filter(pair => pair.kind !== 'dyed' && pair.kind !== 'undyed')
          .map(pair => [pair]),
      ]
    : duplicateBoth
      ? [pairs]
      : pairs.map(pair => [pair]);
  const split = mergeVariants || pairs.some(pair => pair.kind === 'both');
  const sizeUnit = parcelUnit(procurement);
  const labels = [
    'ID Offer',
    'Status',
    'Delivery Basis',
    'Operational Tolerances',
    'Incoterm - Route',
    'Loading Port',
    'Port 1',
    'Port 2',
    'Port 3',
    ...(split
      ? [
          'Alpha dyed',
          'Alpha undyed',
          'Alpha dyed (improvement)',
          'Alpha undyed (improvement)',
        ]
      : ['Alpha', 'Alpha (improvement)']),
  ];

  return (
    <RoomPanel
      title='Bid specification'
      description='Offer terms and alpha for this partner'
      icon='ri-price-tag-3-line'
    >
      <div className='table-responsive'>
        <Table className='neg-spec-table align-middle mb-0'>
          <SpecHead labels={labels} />
          <tbody>
            {groups.map((group, index) => {
              const { detail } = group[0];
              const pick = field =>
                group.find(
                  pair => pair.kind === field || pair.kind === 'both'
                ) || (field === 'alpha' ? group[0] : null);
              const alphaValue = field => {
                const hit = pick(field);
                if (!hit) return '';
                return (
                  baseAlpha(hit.detail, field) ||
                  (hit.kind === field ? baseAlpha(hit.detail, 'alpha') : '')
                );
              };
              const improvement = field => {
                const hit = pick(field);
                if (!hit) return '';
                const specific = text(
                  getAlphaImprovementFieldValue(hit.detail, kind, field, {
                    fallbackToBase: false,
                  })
                );
                const asAlpha =
                  hit.kind === field
                    ? text(
                        getAlphaImprovementFieldValue(
                          hit.detail,
                          kind,
                          'alpha',
                          { fallbackToBase: false }
                        )
                      )
                    : '';
                return specific || asAlpha || text(hit.line?.mitraLastOffer);
              };
              const ports = buildPortSlots(detail, {
                counter,
                unit: sizeUnit,
              });
              const incoterm = counter
                ? formatIncotermRoute(
                    detail.counterIncoterm ?? detail.incoterm,
                    detail.counterRoute ?? detail.route
                  )
                : detail.incotermRoute ||
                  formatIncotermRoute(detail.incoterm, detail.route);
              const basisRaw = counter
                ? (detail.counterDeliveryBasis ??
                  detail.counterParcelSize ??
                  detail.counterBasis ??
                  detail.deliveryBasis ??
                  detail.parcelSize ??
                  detail.basis)
                : (detail.deliveryBasis ?? detail.parcelSize ?? detail.basis);
              const basis =
                basisRaw == null || basisRaw === ''
                  ? null
                  : formatParcelSize(basisRaw, sizeUnit);
              const loading = counter
                ? (detail.counterLoadingPort ??
                  detail.loadingPort ??
                  detail.port1)
                : (detail.loadingPort ?? detail.port1);
              const capable = group.some(pair =>
                capableFlag(
                  pair.line?.isCapable ??
                    pair.detail.isCapable ??
                    partner?.isCapable ??
                    partner?.bidSpec?.isCapable
                )
              );

              return (
                <tr key={group.map(pair => pair.line?.id).join('-') || index}>
                  <td className='fw-medium'>
                    {displayValue(partner?.idOffer)}
                  </td>
                  <td>
                    <BidStatus counter={counter} capable={capable} />
                  </td>
                  <td>{displayValue(basis)}</td>
                  <td>
                    <OperationalToleranceSectionValue
                      procurement={offerToleranceProcurement(
                        procurement,
                        partner,
                        null
                      )}
                    />
                  </td>
                  <td>{displayValue(incoterm === '-' ? null : incoterm)}</td>
                  <td>{formatPortLocation(loading)}</td>
                  {ports.map((port, portIndex) => (
                    <td key={`port-${portIndex}`}>
                      <PortCell port={port} />
                    </td>
                  ))}
                  {split ? (
                    <>
                      <td>{showAlpha(alphaValue('dyed'), unit)}</td>
                      <td>{showAlpha(alphaValue('undyed'), unit)}</td>
                      <td>{showAlpha(improvement('dyed'), unit)}</td>
                      <td>{showAlpha(improvement('undyed'), unit)}</td>
                    </>
                  ) : (
                    <>
                      <td>{showAlpha(alphaValue('alpha'), unit)}</td>
                      <td>{showAlpha(improvement('alpha'), unit)}</td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    </RoomPanel>
  );
};

export default BidSpecTable;
